/**
 * Storage wrappers that never throw. Safari private mode, disabled cookies and
 * SSR all make `localStorage` access blow up; nothing here is important enough
 * to break the desktop over.
 */

function safeStorage(kind: 'local' | 'session'): Storage | null {
  try {
    const s = kind === 'local' ? window.localStorage : window.sessionStorage;
    const probe = '__teonio_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

const local = typeof window === 'undefined' ? null : safeStorage('local');
const session = typeof window === 'undefined' ? null : safeStorage('session');

export const storage = {
  get(key: string): string | null {
    try {
      return local?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      local?.setItem(key, value);
    } catch {
      /* quota or private mode — ignore */
    }
  },
  remove(key: string): void {
    try {
      local?.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export const sessionStore = {
  get(key: string): string | null {
    try {
      return session?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      session?.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string): void {
    try {
      session?.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

/** A `StateStorage` shim for zustand's `persist` middleware. */
export const zustandStorage = {
  getItem: (name: string) => storage.get(name),
  setItem: (name: string, value: string) => storage.set(name, value),
  removeItem: (name: string) => storage.remove(name),
};
